import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { serverFilterId, timeRangeFilterId } from '../../filters';

const title = t`Server Network I/O Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['avgNetworkIn', 'avgNetworkOut'],
    },
};

export function useChartConfig() {
    const networkInColor = useMetricColor('networkIn');
    const networkOutColor = useMetricColor('networkOut');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (%)`,
                    },
                    ceiling: 200,
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    forceDecimals: true,
                    units: '%',
                    chartType: 'spline',
                    key: 'avgNetworkIn',
                    label: t`In`,
                    color: networkInColor,
                    multiplier: 1,
                },
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    forceDecimals: true,
                    units: '%',
                    chartType: 'spline',
                    key: 'avgNetworkOut',
                    label: t`Out`,
                    color: networkOutColor,
                    multiplier: 1,
                },
            ],
        }),
        [networkInColor, networkOutColor]
    );
}

function ServerNetworkUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig();
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

ServerNetworkUtilizationPanel.propTypes = panelPropTypes;

ServerNetworkUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'ServerNetworkUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Network utilization trend for a selected server`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerNetworkUtilizationPanel);
